import { useState } from "react";
import type { Business, SubscriptionDiscount } from "@cliqsuite/db";
import type { Plan } from "../lib/adminTypes";
import type { DiscountFormValue } from "../state/AdminDataContext";
import { Chips, Field, Modal, SwitchRow } from "./Modal";

export function DiscountModal({
  discount,
  businesses,
  plans,
  onClose,
  onSubmit,
  onDelete,
}: {
  discount: SubscriptionDiscount | null;
  businesses: Business[];
  plans: Plan[];
  onClose: () => void;
  onSubmit: (form: DiscountFormValue) => void;
  onDelete?: () => void;
}) {
  const [scope, setScope] = useState<"global" | "business">(discount?.scope ?? "global");
  const [businessId, setBusinessId] = useState(discount?.business_id ?? businesses[0]?.id ?? "");
  const [planId, setPlanId] = useState(discount?.plan_id ?? "");
  const [discountType, setDiscountType] = useState<"percent" | "flat">(discount?.discount_type ?? "percent");
  const [value, setValue] = useState(String(discount?.value ?? 10));
  const [label, setLabel] = useState(discount?.label ?? "");
  const [expiresAt, setExpiresAt] = useState(discount?.expires_at ?? "");
  const [isActive, setIsActive] = useState(discount?.is_active ?? true);

  const valueNum = Math.max(1, Math.round(Number(value) || 0));
  const valid = scope === "global" || !!businessId;

  const submit = () => {
    if (!valid) return;
    onSubmit({
      scope,
      business_id: scope === "business" ? businessId : null,
      plan_id: planId || null,
      discount_type: discountType,
      value: discountType === "percent" ? Math.min(100, valueNum) : valueNum,
      label: label.trim(),
      is_active: isActive,
      expires_at: expiresAt || null,
    });
  };

  return (
    <Modal
      onClose={onClose}
      width={500}
      title={discount ? "Edit discount" : "New discount"}
      sub="Applied automatically, no code needed"
      footer={
        <>
          {onDelete && (
            <button type="button" className="o-btn o-btn--danger" onClick={onDelete}>
              <span className="ms" aria-hidden="true">delete</span>
              Delete
            </button>
          )}
          <span style={{ flex: 1 }} />
          <button type="button" className="o-btn o-btn--grey" onClick={onClose}>
            Cancel
          </button>
          <button type="button" className="o-btn o-btn--ink" onClick={submit} disabled={!valid}>
            <span className="ms" aria-hidden="true">check</span>
            {discount ? "Save changes" : "Create discount"}
          </button>
        </>
      }
    >
      <Field label="Label (optional)">
        <input className="o-input" aria-label="Label (optional)" value={label} onChange={(e) => setLabel(e.target.value)} placeholder="e.g. Launch promo" />
      </Field>
      <Chips
        label="Scope"
        value={scope}
        onChange={setScope}
        options={[
          { value: "global", label: "All businesses" },
          { value: "business", label: "One business" },
        ]}
      />
      {scope === "business" && (
        <Field label="Business">
          <select className="o-input" aria-label="Business" value={businessId} onChange={(e) => setBusinessId(e.target.value)}>
            {businesses.length === 0 && <option value="">No businesses yet</option>}
            {businesses.map((b) => (
              <option key={b.id} value={b.id}>
                {b.name}
              </option>
            ))}
          </select>
        </Field>
      )}
      <Chips label="Plan" value={planId} onChange={setPlanId} options={[{ value: "", label: "All plans" }, ...plans.map((p) => ({ value: p.id, label: p.name }))]} />
      <div className="o-fields2">
        <Chips
          label="Type"
          value={discountType}
          onChange={setDiscountType}
          options={[
            { value: "percent", label: "% off" },
            { value: "flat", label: "MMK off" },
          ]}
        />
        <Field label="Discount">
          <input className="o-input num" aria-label="Discount" type="number" min={1} max={discountType === "percent" ? 100 : undefined} value={value} onChange={(e) => setValue(e.target.value)} />
        </Field>
      </div>
      <Field label="Expires (optional)">
        <input className="o-input" aria-label="Expires (optional)" type="date" value={expiresAt} onChange={(e) => setExpiresAt(e.target.value)} />
      </Field>
      <SwitchRow label="Active" checked={isActive} onChange={setIsActive} />
    </Modal>
  );
}
